import { SetStateAction, useEffect, useRef } from "react";
import { useLeadTags } from "shared/app/LeadTags/hooks/useLeadTags";
import { LeadsFilters } from "shared/app/LeadsTableView/types";

type TagFilter = { id: string; name: string };

// Drops tags deleted since the filter was set and follows renames. Returns null
// when the filter is already current.
const reconcileTagFilter = (
  selected: TagFilter[] | undefined,
  library: { id: number; name: string }[]
): TagFilter[] | null => {
  if (!selected?.length) return null;
  const namesById = new Map(library.map((tag) => [String(tag.id), tag.name]));
  const reconciled = selected.flatMap((tag) => {
    const name = namesById.get(tag.id);
    return name === undefined ? [] : [{ id: tag.id, name }];
  });
  const isCurrent =
    reconciled.length === selected.length &&
    reconciled.every((tag, index) => tag.name === selected[index].name);
  return isCurrent ? null : reconciled;
};

/**
 * Keeps the Tags filter (URL, badges, saved views) in step with the master list:
 * a deleted tag leaves the filter, a renamed one follows its new name. A tag missing
 * from the cached list may just be new (created elsewhere after this page loaded), so
 * it is only dropped once a list fetched after noticing still lacks it.
 */
export const useReconcileTagFilter = ({
  tags,
  setFilters
}: {
  tags: TagFilter[] | undefined;
  setFilters: (updater: SetStateAction<LeadsFilters | undefined>) => void;
}) => {
  const {
    data: library,
    isFetching,
    dataUpdatedAt,
    refetch
  } = useLeadTags({
    enabled: Boolean(tags?.length)
  });
  // When a missing tag was first noticed; null while nothing awaits a fresh list.
  const missingSinceRef = useRef<number | null>(null);

  useEffect(() => {
    if (!library || isFetching) return;
    const reconciled = reconcileTagFilter(tags, library);
    if (!reconciled) {
      missingSinceRef.current = null;
      return;
    }

    const dropsTags = reconciled.length < (tags?.length ?? 0);
    if (dropsTags && missingSinceRef.current === null) {
      missingSinceRef.current = Date.now();
      void refetch();
      return;
    }
    if (dropsTags && dataUpdatedAt < (missingSinceRef.current ?? 0)) return;

    missingSinceRef.current = null;
    setFilters((prev) => ({ ...prev, tags: reconciled }));
  }, [library, isFetching, dataUpdatedAt, refetch, tags, setFilters]);
};
